/** Use to group chains by family, like EVM vs Solana. */
export const SUPPORTED_CHAIN_TYPES = [
    "EVM",
    "Solana",
    "Bitcoin",
    "Cosmos",
    "Starknet",
];
/**
 * Named-literal mirror of SUPPORTED_CHAIN_TYPES for call sites that read as
 * `CHAIN_TYPE.EVM` (guards, switch arms, error messages). Values stay in
 * lockstep with SUPPORTED_CHAIN_TYPES via the `satisfies` constraint.
 */
export const CHAIN_TYPE = {
    EVM: "EVM",
    SOLANA: "Solana",
    BITCOIN: "Bitcoin",
    STARKNET: "Starknet",
    COSMOS: "Cosmos",
};
/** Main chain list used to build enums, maps, and normalization helpers. */
export const SUPPORTED_CHAINS = [
    { slug: "ethereum", type: "EVM", aliases: [] },
    { slug: "polygon", type: "EVM", aliases: ["polygon-pos"] },
    { slug: "bsc", type: "EVM", aliases: ["binance-smart-chain"] },
    { slug: "optimism", type: "EVM", aliases: ["optimistic-ethereum"] },
    { slug: "base", type: "EVM", aliases: [] },
    { slug: "avalanche", type: "EVM", aliases: [] },
    { slug: "arbitrum", type: "EVM", aliases: ["arbitrum-one"] },
    { slug: "gnosis", type: "EVM", aliases: ["xdai"] },
    { slug: "berachain", type: "EVM", aliases: [] },
    { slug: "moonbeam", type: "EVM", aliases: [] },
    { slug: "monad", type: "EVM", aliases: [] },
    { slug: "linea", type: "EVM", aliases: [] },
    { slug: "hyperliquid", type: "EVM", aliases: ["hyperevm"] },
    { slug: "scroll", type: "EVM", aliases: [] },
    { slug: "plasma", type: "EVM", aliases: [] },
    { slug: "xlayer", type: "EVM", aliases: ["x-layer"] },
    { slug: "solana", type: "Solana", aliases: [] },
    { slug: "bitcoin", type: "Bitcoin", aliases: [] },
    { slug: "starknet", type: "Starknet", aliases: [] },
    { slug: "cosmoshub", type: "Cosmos", aliases: ["cosmos"] },
    { slug: "osmosis", type: "Cosmos", aliases: [] },
    { slug: "axelar", type: "Cosmos", aliases: [] },
    { slug: "noble", type: "Cosmos", aliases: ["nobleblocks"] },
    { slug: "agoric", type: "Cosmos", aliases: [] },
];
/** Use when an endpoint should only accept the public chain slugs. */
export const SUPPORTED_CHAIN_SLUGS = SUPPORTED_CHAINS.map((chain) => chain.slug);
/** Use when something should only run on EVM chains. */
export const EVM_CHAIN_SLUGS = SUPPORTED_CHAINS.filter((chain) => chain.type === "EVM").map((chain) => chain.slug);
/** Use when something should only run on Starknet chains. */
export const STARKNET_CHAIN_SLUGS = SUPPORTED_CHAINS.filter((chain) => chain.type === "Starknet").map((chain) => chain.slug);
/** Use when something should only run on Solana chains. */
export const SOLANA_CHAIN_SLUGS = SUPPORTED_CHAINS.filter((chain) => chain.type === "Solana").map((chain) => chain.slug);
/** Use when something should only run on Cosmos chains. */
export const COSMOS_CHAIN_SLUGS = SUPPORTED_CHAINS.filter((chain) => chain.type === "Cosmos").map((chain) => chain.slug);
/** Use when something should only run on the pre-Cosmos generic chain set. */
export const NON_COSMOS_CHAIN_SLUGS = SUPPORTED_CHAINS.filter((chain) => chain.type !== "Cosmos").map((chain) => chain.slug);
/** Stable EVM chain subset used by integration tests for multi-chain contract coverage. */
export const CORE_REGRESSION_CHAINS = [
    "ethereum",
    "polygon",
    "base",
    "arbitrum",
    "optimism",
];
/** Pinned heights used by integration regression tests so payload checks stay stable. */
export const REGRESSION_BLOCK_HEIGHTS = {
    ethereum: 23_000_000,
    polygon: 73_000_000,
    base: 31_000_000,
    arbitrum: 320_000_000,
    optimism: 135_000_000,
};
/** Use to turn legacy or DB chain names into the public slug. */
export const DB_IDENTIFIER_TO_CHAIN_SLUG = {
    "polygon-pos": "polygon",
    "binance-smart-chain": "bsc",
    "optimistic-ethereum": "optimism",
    "arbitrum-one": "arbitrum",
    xdai: "gnosis",
    cosmos: "cosmoshub",
    nobleblocks: "noble",
    hyperevm: "hyperliquid",
};
const buildChainMap = (chains) => Object.fromEntries(chains.map((chain) => [chain.slug, chain]));
/** Use for fast lookup when code already has a chain slug and needs the full chain definition. */
export const SUPPORTED_CHAIN_MAP = buildChainMap(SUPPORTED_CHAINS);
